import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { userQueryKeys, useUserStore } from '@entities/user';
import { billingApi } from './billingApi';
import type {
  AccountBillingSummary,
  AssistantBillingSummary,
  BillingOverviewResponse,
  PaymentIntentStatusResponse,
  TopupCreateRequest,
  TopupCreateResponse,
  TransactionFilters,
  UserTransactionsResponse,
} from '../model/types';

export const billingQueryKeys = {
  all: ['billing'] as const,
  account: () => [...billingQueryKeys.all, 'account'] as const,
  summary: (assistantId: string) => [...billingQueryKeys.all, 'summary', assistantId] as const,
  paymentIntent: (intentId: string) =>
    [...billingQueryKeys.all, 'payment-intent', intentId] as const,
  transactions: (filters?: TransactionFilters) =>
    [...billingQueryKeys.all, 'transactions', filters ?? {}] as const,
  overview: (filters?: Pick<TransactionFilters, 'date_from' | 'date_to'>) =>
    [...billingQueryKeys.all, 'overview', filters ?? {}] as const,
};

/**
 * GET /billing/summary — свежий баланс аккаунта (V2-1). При каждом открытии
 * страницы перезапрашивается и синхронизирует стор юзера.
 */
export function useAccountSummary() {
  const setBalance = useUserStore((s) => s.setBalance);
  const query = useQuery<AccountBillingSummary, Error>({
    queryKey: billingQueryKeys.account(),
    queryFn: () => billingApi.getAccountSummary(),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  useEffect(() => {
    if (query.data) setBalance(query.data.balance_rub);
  }, [query.data, setBalance]);
  return query;
}

/**
 * GET /billing/transactions — общая история всех ассистентов юзера.
 * B-MVP-4a: фильтры (assistant_id / kind / date_from / date_to) входят
 * в ключ кэша, при смене фильтра — отдельный refetch.
 */
export function useUserTransactions(limit = 50, filters: TransactionFilters = {}) {
  return useQuery<UserTransactionsResponse, Error>({
    queryKey: billingQueryKeys.transactions(filters),
    queryFn: () => billingApi.getUserTransactions(limit, 0, filters),
    staleTime: 10_000,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
  });
}

/**
 * GET /billing/overview — агрегированная сводка биллинга (B-MVP-4a).
 * Период (date_from/date_to) входит в ключ кэша.
 */
export function useBillingOverview(
  filters: Pick<TransactionFilters, 'date_from' | 'date_to'> = {},
) {
  return useQuery<BillingOverviewResponse, Error>({
    queryKey: billingQueryKeys.overview(filters),
    queryFn: () => billingApi.getOverview(filters),
    staleTime: 10_000,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
  });
}

/** GET /billing/assistant/{id}/summary — баланс + история. */
export function useAssistantBillingSummary(assistantId: string | undefined) {
  return useQuery<AssistantBillingSummary, Error>({
    queryKey: billingQueryKeys.summary(assistantId ?? ''),
    queryFn: () => billingApi.getAssistantSummary(assistantId!),
    enabled: !!assistantId,
    staleTime: 10_000,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 1;
    },
  });
}

/**
 * GET /billing/payment-intents/{id} — статус с polling'ом.
 *
 * Используется на странице биллинга после возврата с ЮKассы
 * (URL ?payment_intent=X). Polling раз в 2 сек пока status === 'pending'.
 *
 * refetchInterval — функция: пока pending, опрашиваем; как только финальный
 * статус — останавливаемся (return false).
 *
 * enabled управляется снаружи — включаем только когда есть intentId в URL.
 */
export function usePaymentIntentStatus(intentId: string | undefined) {
  return useQuery<PaymentIntentStatusResponse, Error>({
    queryKey: billingQueryKeys.paymentIntent(intentId ?? ''),
    queryFn: () => billingApi.getPaymentIntentStatus(intentId!),
    enabled: !!intentId,
    // Polling пока pending
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000; // ещё не загрузили — пробуем
      return data.status === 'pending' ? 2000 : false;
    },
    // Не кэшируем надолго — статус меняется
    staleTime: 0,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });
}

/**
 * POST /billing/topup/create — создать платёж.
 *
 * onSuccess НЕ инвалидирует summary — баланс изменится только после
 * webhook'а (когда юзер оплатит). Здесь просто получаем confirmation_url.
 */
export function useCreateTopupMutation() {
  return useMutation<TopupCreateResponse, Error, TopupCreateRequest>({
    mutationFn: (payload) => billingApi.createTopup(payload),
  });
}

/**
 * Хелпер: инвалидировать всё что связано с балансом ассистента.
 * Вызывается когда платёж подтвердился (succeeded) — чтобы свежий
 * баланс подтянулся в summary, историю, сводку и user.assistants (сайдбар).
 *
 * transactions/overview инвалидируем префиксно (['billing','transactions'] /
 * ['billing','overview']) — чтобы накрыть все варианты фильтров/периодов.
 */
export function useInvalidateBillingOnSuccess() {
  const qc = useQueryClient();
  return (assistantId: string) => {
    qc.invalidateQueries({ queryKey: billingQueryKeys.summary(assistantId) });
    qc.invalidateQueries({ queryKey: [...billingQueryKeys.all, 'transactions'] });
    qc.invalidateQueries({ queryKey: [...billingQueryKeys.all, 'overview'] });
    qc.invalidateQueries({ queryKey: billingQueryKeys.account() });
    qc.invalidateQueries({ queryKey: userQueryKeys.me() });
  };
}
